import React, { useCallback, useState } from 'react';
import { Grid } from '@mui/system';
import { COMMERCIAL_STATE_OPTIONS, HEALTHCHECK_NAMES, useNewProjectService } from 'domain/shared/newProject';
import HealthcheckRow from '../HealthcheckRow';
import { HEALTHCHECK_LABELS } from './constants';
import { getHealthcheckByName, HealthcheckMessage } from '../../shared/healthcheck';
import { Modal } from 'framework/ui/ui-builder';
import { EnumDropdown, FormFieldsData, ModalForm } from 'framework/form';
import useReconcileProjectNotifications, {
  PROJECT_NOTIFICATION_SCOPE,
} from '../../shared/notifications/useReconcileProjectNotifications';
import { ControlData } from '../types';

type CommercialHealthCheckProps = {
  data: ControlData;
  refetchControlsData: () => Promise<unknown>;
};

const CommercialHealthchecks = ({ data, refetchControlsData }: CommercialHealthCheckProps) => {
  const healthChecks = data?.health?.healthChecks || [];
  const [openModal, setIsModalOpen] = useState(false);
  const { updateState, commercialStateValidationSchema } = useNewProjectService();
  const reconcileNotifications = useReconcileProjectNotifications();

  const commercialStateHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.PROJECT_COMMERCIAL_STATE);
  const commercialStateStatusHealthcheck = getHealthcheckByName(
    healthChecks,
    HEALTHCHECK_NAMES.PROJECT_COMMERCIAL_STATE_STATUS,
  );
  const assumptionHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.ASSUMPTIONS);
  const billingHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.BILLING);
  const sowExpiresHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.CONTRACT_RENEWAL);
  const budgetStateHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.BUDGET_STATE);
  const changeRequestsHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.CHANGE_REQUESTS);
  const risksHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.COMMERCIAL_RISKS);

  const submitCommercialStateFormFunc = useCallback(
    async (stateData: FormFieldsData) => {
      const updatedState = {
        commercialState: stateData.projectDimension?.commercialState,
      };
      const result = await updateState(data.id, updatedState);
      await reconcileNotifications(String(data.id), [PROJECT_NOTIFICATION_SCOPE.COMMERCIAL_STATE]).catch(
        () => undefined,
      );
      return result;
    },
    [updateState, data.id, reconcileNotifications],
  );

  return (
    <>
      <Grid container direction={'column'} size={12} pl={2}>
        <HealthcheckRow
          label={HEALTHCHECK_LABELS.COMMERCIAL_STATE}
          isUpdateVisible
          lastUpdateValue={
            commercialStateHealthcheck?.latestEntityModificationDate ?? commercialStateHealthcheck?.zoneSince
          }
          onEdit={() => setIsModalOpen(true)}
        >
          <Grid direction="column">
            <HealthcheckMessage healthcheck={commercialStateHealthcheck} />
            <HealthcheckMessage healthcheck={commercialStateStatusHealthcheck} />
          </Grid>
        </HealthcheckRow>
        <HealthcheckRow
          label={HEALTHCHECK_LABELS.ASSUMPTION}
          lastUpdateValue={assumptionHealthcheck?.latestEntityModificationDate ?? assumptionHealthcheck?.zoneSince}
        >
          <HealthcheckMessage healthcheck={assumptionHealthcheck} />
        </HealthcheckRow>
        {data?.accountData?.accountId && (
          <HealthcheckRow
            label={HEALTHCHECK_LABELS.BILLING}
            lastUpdateValue={billingHealthcheck?.latestEntityModificationDate ?? billingHealthcheck?.zoneSince}
          >
            <HealthcheckMessage healthcheck={billingHealthcheck} />
          </HealthcheckRow>
        )}
        {(sowExpiresHealthcheck || budgetStateHealthcheck) && (
          <HealthcheckRow
            label={HEALTHCHECK_LABELS.SOW_EXPIRES}
            lastUpdateValue={
              data?.accountData?.sowId
                ? (sowExpiresHealthcheck?.latestEntityModificationDate ?? sowExpiresHealthcheck?.zoneSince)
                : (budgetStateHealthcheck?.latestEntityModificationDate ?? budgetStateHealthcheck?.zoneSince)
            }
          >
            <HealthcheckMessage
              healthcheck={data?.accountData?.sowId ? sowExpiresHealthcheck : budgetStateHealthcheck}
            />
          </HealthcheckRow>
        )}
        {changeRequestsHealthcheck && (
          <HealthcheckRow
            label={HEALTHCHECK_LABELS.CHANGE_REQUESTS}
            lastUpdateValue={
              changeRequestsHealthcheck?.latestEntityModificationDate ?? changeRequestsHealthcheck?.zoneSince
            }
          >
            <HealthcheckMessage healthcheck={changeRequestsHealthcheck} />
          </HealthcheckRow>
        )}
        <HealthcheckRow label={HEALTHCHECK_LABELS.RISKS} lastUpdateValue={risksHealthcheck?.zoneSince}>
          <HealthcheckMessage healthcheck={risksHealthcheck} />
        </HealthcheckRow>
      </Grid>
      <Modal isOpen={openModal} onToggle={() => setIsModalOpen(false)} title="Commercial State update">
        <ModalForm
          closeModal={() => setIsModalOpen(false)}
          modalOpen={openModal}
          submitFormFunc={submitCommercialStateFormFunc}
          getValidationSchemaFunc={commercialStateValidationSchema}
          afterSubmit={{
            execute: () => refetchControlsData(),
          }}
          getDataFunc={async () => data}
        >
          <EnumDropdown
            options={COMMERCIAL_STATE_OPTIONS}
            source="projectDimension.commercialState"
            label="Commercial state"
          />
        </ModalForm>
      </Modal>
    </>
  );
};

export default CommercialHealthchecks;
